import React, { useState, useEffect } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import { useSelector } from "react-redux";
import useThemeColor from "../../../hooks/useThemeColor";
import CustomModal from "./customModal";
import {
  SelectionModalHeaderActions,
  SelectionModalEmptyCTA,
  SelectionModalRefreshButton,
} from "./SelectionModalQuickLinks";

export default function MenuGroupSelectionModal({
  showModal,
  setShowModal,
  showButton,
  onSubmit,
  setSelectedMenuGroups,
  selectedMenuGroups,
  menus,
  onRefresh,
  listRefreshLoading = false,
  isMultiSelect = true,
  modalTitle,
  modalSubTitle,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedMenus, setExpandedMenus] = useState({});
  const [tempSelectedGroups, setTempSelectedGroups] = useState([]);
  const [filteredMenus, setFilteredMenus] = useState([]);
  const isDarkMode = useDarkMode();
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  const safeSelectedGroups = Array.isArray(selectedMenuGroups) ? selectedMenuGroups : [];

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  

  useEffect(() => {
    if (showModal) {
      setTempSelectedGroups(safeSelectedGroups);
      setFilteredMenus(menus || []);
    }
  }, [showModal, safeSelectedGroups, menus]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredMenus(menus || []);
    } else {
      const filtered = (menus || []).map((menu) => ({
        ...menu,
        menuGroups: (menu.menuGroups || []).filter((group) =>
          group.menuGroup_Name?.toLowerCase().includes(searchTerm.trim().toLowerCase())
        ),
      })).filter((menu) => menu.menuGroups.length > 0 || menu.menu_Name?.toLowerCase().includes(searchTerm.trim().toLowerCase()));
      setFilteredMenus(filtered);
    }
  }, [searchTerm, menus]);

  const toggleMenu = (menuId) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuId]: !prev[menuId],
    }));
  };

  const handleMenuGroupSelection = (group) => {
    setTempSelectedGroups((prev) => {
      const safePrev = Array.isArray(prev) ? prev : [];
      const groupId = group.menuGroupID;
      if (safePrev.some((g) => g.menuGroupID === groupId)) {
        return safePrev.filter((g) => g.menuGroupID !== groupId);
      }
      return [...safePrev, group];
    });
  };

  const handleSubmit = () => {
    if (typeof onSubmit === "function") {
      setSelectedMenuGroups(tempSelectedGroups);
      onSubmit(tempSelectedGroups);
    }
    setShowModal(false);
  };

  const RenderMenuGroupCard = ({ group }) => {
    const isChecked = tempSelectedGroups.some((g) => g.menuGroupID === group.menuGroupID);

    return (
      <div 
        className="menu-group-card"
        style={{
          background: isChecked 
            ? (isDarkMode ? `${themeColor}20` : `${themeColor}15`) 
            : (isDarkMode ? '#404040' : '#ffffff'),
          borderRadius: '12px',
          padding: '16px 20px',
          border: isChecked 
            ? `2px solid ${themeColor}` 
            : (isDarkMode ? '2px solid #555' : '2px solid #e9ecef'),
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          position: 'relative',
          boxShadow: isChecked 
            ? `0 4px 12px ${themeColor}25` 
            : (isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)'),
          marginBottom: '12px',
          marginLeft: '24px'
        }}
        onClick={() => handleMenuGroupSelection(group)}
        onMouseEnter={(e) => {
          if (!isChecked) {
            e.currentTarget.style.borderColor = themeColor;
            e.currentTarget.style.boxShadow = `0 4px 12px ${themeColor}25`;
            e.currentTarget.style.transform = 'translateY(-2px)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isChecked) {
            e.currentTarget.style.borderColor = isDarkMode ? '#555' : '#e9ecef';
            e.currentTarget.style.boxShadow = isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)';
            e.currentTarget.style.transform = 'translateY(0)';
          }
        }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            {/* Menu Group Info */}
            <div>
              <span 
                style={{ 
                  fontSize: '14px', 
                  fontWeight: '600',
                  color: isChecked ? themeColor : (isDarkMode ? '#ffffff' : '#495057'),
                  display: 'block',
                  marginBottom: '2px'
                }}
              >
                {group.menuGroup_Name}
              </span>
              {group.menuGroup_Publish === false && (
                <span 
                  style={{
                    background: isDarkMode ? '#4a1a1a' : '#fff3cd',
                    color: isDarkMode ? '#ff6b6b' : '#856404',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '500'
                  }}
                >
                  Inactive
                </span>
              )}
            </div>
          </div>
          
          {/* Custom Checkbox */}
          <div 
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '6px',
              border: isChecked ? `2px solid ${themeColor}` : (isDarkMode ? '2px solid #666' : '2px solid #e9ecef'),
              background: isChecked ? themeColor : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
          >
            {isChecked && (
              <i 
                className="fa fa-check" 
                style={{ 
                  fontSize: '12px', 
                  color: 'white',
                  fontWeight: 'bold'
                }}
              ></i>
            )}
          </div>
        </div>
      </div>
    );
  };

  const RenderMenuCard = ({ menu }) => {
    const isExpanded = expandedMenus[menu.menu_ID];

    return (
      <div style={{ marginBottom: '16px' }}>
        {/* Menu Header Card */}
        <div 
          className="menu-header-card"
          style={{
            background: isDarkMode ? '#404040' : '#f8f9fa',
            borderRadius: '12px',
            padding: '16px 20px',
            border: isDarkMode ? '2px solid #555' : '2px solid #e9ecef',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            position: 'relative',
            boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)'
          }}
          onClick={() => toggleMenu(menu.menu_ID)}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = themeColor;
            e.currentTarget.style.boxShadow = `0 4px 12px ${themeColor}25`;
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = isDarkMode ? '#555' : '#e9ecef';
            e.currentTarget.style.boxShadow = isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              {/* Menu Info */}
              <div>
                <span 
                  style={{ 
                    fontSize: '14px', 
                    fontWeight: '600',
                    color: isDarkMode ? '#ffffff' : '#495057',
                    display: 'block',
                    marginBottom: '2px'
                  }}
                >
                  {menu.menu_Name}
                </span>
                {menu.menu_Publish === false && (
                  <span 
                    style={{
                      background: isDarkMode ? '#4a1a1a' : '#fff3cd',
                      color: isDarkMode ? '#ff6b6b' : '#856404',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: '500'
                    }}
                  >
                    Inactive
                  </span>
                )}
              </div>
            </div>
            
            {/* Expand/Collapse Icon */}
            <div 
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: isDarkMode ? '#555' : '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: isDarkMode ? '1px solid #666' : '1px solid #e9ecef',
                transition: 'all 0.2s ease'
              }}
            >
              <i 
                className={`fa fa-chevron-${isExpanded ? "up" : "down"}`}
                style={{ 
                  fontSize: '14px', 
                  color: themeColor,
                  fontWeight: 'bold'
                }}
              ></i>
            </div>
          </div>
        </div>

        {/* Menu Groups */}
        {isExpanded && (
          <div style={{ marginTop: '8px' }}>
            {menu.menuGroups && menu.menuGroups.length > 0 ? (
              menu.menuGroups.map((group) => (
                <RenderMenuGroupCard key={group.menuGroupID} group={group} />
              ))
            ) : (
              <div 
                style={{
                  background: isDarkMode ? '#404040' : '#f8f9fa',
                  borderRadius: '12px',
                  padding: '20px',
                  border: isDarkMode ? '2px dashed #555' : '2px dashed #e9ecef',
                  marginLeft: '24px',
                  textAlign: 'center'
                }}
              >
                <div 
                  style={{
                    width: '40px',
                    height: '40px',
                    background: isDarkMode ? '#555' : '#ffffff',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 12px',
                    border: isDarkMode ? '1px solid #666' : '1px solid #e9ecef'
                  }}
                >
                  <i className="fa fa-list" style={{ fontSize: '18px', color: isDarkMode ? '#ffffff' : '#6c757d' }}></i>
                </div>
                <p style={{ color: isDarkMode ? '#ffffff' : '#6c757d', fontSize: '12px', margin: 0 }}>
                  No menu groups available
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  const hasAnyMenuGroups = (menus || []).some(
    (m) => Array.isArray(m.menuGroups) && m.menuGroups.length > 0
  );
  const menuDataTrulyEmpty =
    !menus || menus.length === 0 || !hasAnyMenuGroups;

  return (
    <div>
      <CustomModal
        isOpen={showModal}
        title={modalTitle}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmit}
        showButton={showButton}
        showSubmitButton={true}
        showCancelButton={false}
        isCentered={true}
        submitButtonText="Select"
        headerActions={<SelectionModalHeaderActions variant="menuGroup" />}
      >
        <p style={{ color: isDarkMode ? '#ffffff' : '#6c757d', marginBottom: '20px' }}>{modalSubTitle}</p>
        
        {/* Search Input */}
        <div className="mb-4 d-flex align-items-stretch gap-2">
          <div
            className="flex-grow-1"
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <input
              type="text"
              className="form-control"
              placeholder="Search menus or menu groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: "12px 44px 12px 16px",
                border: isDarkMode ? "2px solid #555" : "2px solid #e9ecef",
                borderRadius: "8px",
                fontSize: "14px",
                transition: "all 0.2s ease",
                background: isDarkMode ? "#2d3436" : "#ffffff",
                color: isDarkMode ? "#ffffff" : "#000000",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = themeColor;
                e.target.style.boxShadow = `0 0 0 3px ${themeColor}20`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = isDarkMode ? "#555" : "#e9ecef";
                e.target.style.boxShadow = "none";
              }}
            />
            <i
              className="fa fa-search"
              style={{
                position: "absolute",
                right: "16px",
                color: isDarkMode ? "#ffffff" : "#6c757d",
                fontSize: "14px",
                pointerEvents: "none",
              }}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                style={{
                  position: "absolute",
                  right: "40px",
                  background: "transparent",
                  border: "none",
                  color: isDarkMode ? "#ffffff" : "#6c757d",
                  cursor: "pointer",
                  padding: "4px",
                  borderRadius: "4px",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isDarkMode ? "#555" : "#f8f9fa";
                  e.currentTarget.style.color = "#dc3545";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = isDarkMode ? "#ffffff" : "#6c757d";
                }}
              >
                <i className="fa fa-times" style={{ fontSize: "12px" }} />
              </button>
            )}
          </div>
          {typeof onRefresh === "function" && (
            <SelectionModalRefreshButton
              onClick={() => onRefresh()}
              loading={listRefreshLoading}
            />
          )}
        </div>

        {/* Menu Cards */}
        <div style={{ height: "450px", overflowY: "auto", paddingRight: '8px' }}>
          {filteredMenus.length > 0 ? (
            <div>
              {filteredMenus.map((menu) => (
                <RenderMenuCard key={menu.menu_ID} menu={menu} />
              ))}
            </div>
          ) : menuDataTrulyEmpty ? (
            <SelectionModalEmptyCTA variant="menuGroup" />
          ) : (
            <div 
              className="text-center py-5"
              style={{
                background: isDarkMode ? '#404040' : '#f8f9fa',
                borderRadius: '12px',
                border: isDarkMode ? '2px dashed #555' : '2px dashed #e9ecef'
              }}
            >
              <div 
                style={{
                  width: '60px',
                  height: '60px',
                  background: isDarkMode ? '#555' : '#ffffff',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  border: isDarkMode ? '1px solid #666' : '1px solid #e9ecef'
                }}
              >
                <i className="fa fa-search" style={{ fontSize: '24px', color: isDarkMode ? '#ffffff' : '#6c757d' }}></i>
              </div>
              <p style={{ color: isDarkMode ? '#ffffff' : '#6c757d', fontSize: '13px', marginBottom: '6px' }}>
                No menus or menu groups found
                {searchTerm.length > 0 ? ` matching "${searchTerm}"` : ""}
              </p>
              {searchTerm.length > 0 && (
                <small style={{ color: isDarkMode ? '#ffffff' : '#6c757d', fontSize: '11px' }}>
                  Try adjusting your search terms
                </small>
              )}
            </div>
          )}
        </div>
      </CustomModal>
    </div>
  );
}